import { ComponentProps } from 'react';
import clsx from 'clsx';
import { OpDataFetchSelect } from 'new-components/DLS/OpDataFetchSelect/OpDataFetchSelect';
import { OpSkeleton } from 'new-components/DLS/OpSkeleton/OpSkeleton';
import { v4 as uuidv4 } from 'uuid';
import { GetterApiEndpointNames } from 'new-components/DLS/OpDataFetchTable/OpDataFetchTable';
import { useOpFormContext } from '../../OpFormContext';
import { OpForm } from '../../OpForm';
import { SelectableValueTypes } from '../../../OpSelect/OpSelect';

type OpFormDataFetchSelectProps<
  Value extends SelectableValueTypes,
  T extends GetterApiEndpointNames,
  SelectData = Awaited<ReturnType<Api.Client[T]>>,
> = Pick<
  ComponentProps<typeof OpForm.Item>,
  | 'name'
  | 'label'
  | 'tooltip'
  | 'rules'
  | 'validateTrigger'
  | 'initialValue'
  | 'validateStatus' // Temp: For SUIR wrapped version
  | 'help' // Temp: For SUIR wrapped version
  | 'required' // Temp: For SUIR wrapped version
> &
  ComponentProps<typeof OpDataFetchSelect<Value, T, SelectData>> & {
    formItemClassName?: string;
    isLoading?: boolean;
  };

export const OpFormDataFetchSelect = <
  Value extends SelectableValueTypes,
  T extends GetterApiEndpointNames,
  SelectData = Awaited<ReturnType<Api.Client[T]>>,
>({
  // Form Item props
  name,
  label,
  tooltip,
  rules,
  validateTrigger,
  formItemClassName,
  initialValue,
  required, // TEMP: For old form component validation
  validateStatus, // TEMP: For old form component validation
  help, // TEMP: For old form component validation

  // For skeleton loading state
  isLoading,

  // Wrapped element props
  disabled,
  testId,
  ...selectProps
}: OpFormDataFetchSelectProps<Value, T, SelectData>) => {
  /** The Form is wrapped in a provider that passes the loading context
   *  to the component */
  const { isDataLoading, isReadOnly } = useOpFormContext();

  const opFormItemTestId = testId
    ? `${testId}-op-form-item`
    : typeof label === 'string'
      ? `${label}-op-form-item`
      : undefined;
  const uuid = uuidv4();
  const opSelectId = `op-data-fetch-select-${uuid}`;

  return (
    <OpForm.Item
      className={clsx('op-form-data-fetch-select', formItemClassName)}
      name={name}
      label={label}
      tooltip={tooltip}
      rules={rules}
      validateTrigger={validateTrigger}
      initialValue={initialValue}
      required={required} // TEMP: For old form component validation
      validateStatus={validateStatus} // TEMP: For old form component validation
      help={help} // TEMP: For old form component validation
      testId={opFormItemTestId}
      {...(!name && { htmlFor: opSelectId })} // If a name isn't passed there is no association between the label and the input. This creates that association.
    >
      {isDataLoading || isLoading ? (
        <OpSkeleton.Input active block />
      ) : (
        <OpDataFetchSelect<Value, T, SelectData>
          disabled={isReadOnly || disabled}
          {...(!name && { id: opSelectId })} // If a name isn't passed there is no association between the label and the input. This creates that association.
          {...selectProps}
          testId={testId}
        />
      )}
    </OpForm.Item>
  );
};
